/*
Newsletter          Default send time (ET)
Weekend Reads       Fridays, noon
Mutual Funds        Thursdays, 9 am
ETF Wrap            Thursdays, 5 pm
Need to Know        Daily, 7:30 am
Distributed Ledger  Daily, 4 pm ET

From here: https://dowjones.atlassian.net/browse/ET-7315
*/
import { utcToZonedTime } from 'date-fns-tz';

import { PublicationSetting } from 'data/generated/graphql';
import { TIMES } from './time';

export const getTimeZoneOffsetByName = (timeZone: string | undefined) => {
  if (!timeZone) return null;
  const str = new Date().toLocaleString('en', { timeZone, timeZoneName: 'longOffset' });
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [_, h, m] = str.match(/([+-]\d+):(\d+)$/) ?? ['', '+00', '00'];
  return (Number(h) * 60 + (Number(h) > 0 ? +m : -m)) * -1;
};

export const getPaddedTime = (date: Date) =>
  `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

export const adjustToUtcTime = (date: number, publicationSetting?: PublicationSetting) => {
  let adjustedPublishUtc = date;
  const publishDateObj = new Date(date);
  const desiredTZOffset = getTimeZoneOffsetByName(publicationSetting?.preferredTimezone.timezone);

  if (desiredTZOffset) {
    const currentTZOffset = publishDateObj.getTimezoneOffset();
    const offsetDifference =
      currentTZOffset < 0 ? (currentTZOffset - desiredTZOffset) * -1 : desiredTZOffset - currentTZOffset;
    adjustedPublishUtc = date + offsetDifference * TIMES.ONE_MINUTE;
  }

  return adjustedPublishUtc;
};

export const getNextQuarterHour = (publicationSetting?: PublicationSetting, initTimestamp: number = Date.now()) => {
  if (!publicationSetting) {
    // No publication setting, just use current time in browser's timezone
    const now = new Date(initTimestamp);
    const minutes = now.getMinutes() + 1;
    const nextQuarter = Math.ceil(minutes / 15) * 15;

    if (nextQuarter === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(nextQuarter);
    }
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now;
  }

  // Get the current time in the preferred timezone
  const nowInPreferredTz = utcToZonedTime(initTimestamp, publicationSetting.preferredTimezone.timezone);

  // Calculate next quarter hour
  const minutes = nowInPreferredTz.getMinutes() + 1;
  const nextQuarter = Math.ceil(minutes / 15) * 15;

  if (nextQuarter === 60) {
    nowInPreferredTz.setHours(nowInPreferredTz.getHours() + 1);
    nowInPreferredTz.setMinutes(0);
  } else {
    nowInPreferredTz.setMinutes(nextQuarter);
  }
  nowInPreferredTz.setSeconds(0);
  nowInPreferredTz.setMilliseconds(0);

  // Return this Date - it represents the time in the preferred timezone
  // When the Calendar renders it, it will show these exact time components
  return nowInPreferredTz;
};

interface NewsletterFilters {
  [currentProperty: string]: {
    [newsletterName: string]: {
      sectionType: string;
    };
  };
}

export const NEWSLETTER_FILTERS: NewsletterFilters = {
  marketwatch: {
    'Distributed Ledger': {
      sectionType: 'Distributed Ledger'
    },
    'Mutual Funds Weekly': {
      sectionType: 'Mutual Funds Weekly'
    },
    'ETF Wrap': {
      sectionType: 'ETF Wrap'
    },
    'Need to Know': {
      sectionType: 'Need to Know'
    }
  }
};
